/**
 * 材料使用记录服务
 */
import api from './api';
import {
  MaterialUsage,
  CreateMaterialUsageData,
  UpdateMaterialUsageData,
  MaterialUsageQueryParams,
  MaterialUsageSummary,
  PaginatedResponse,
  ApiResponse,
} from '../types';

export class MaterialUsageService {
  private static readonly BASE_URL = '/material-usage';

  /**
   * 获取材料使用记录列表
   */
  static async getMaterialUsages(params: MaterialUsageQueryParams = {}): Promise<PaginatedResponse<MaterialUsage>> {
    const queryParams = new URLSearchParams();
    
    if (params.projectId) queryParams.append('projectId', params.projectId);
    if (params.materialName) queryParams.append('materialName', params.materialName);
    if (params.supplierId) queryParams.append('supplierId', params.supplierId);
    if (params.constructionType) queryParams.append('constructionType', params.constructionType);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const url = `${this.BASE_URL}?${queryParams.toString()}`;
    return await api.get(url);
  }

  /**
   * 根据ID获取材料使用记录详情
   */
  static async getMaterialUsageById(id: string): Promise<ApiResponse<MaterialUsage>> {
    return await api.get(`${this.BASE_URL}/${id}`);
  }

  /**
   * 创建材料使用记录
   */
  static async createMaterialUsage(data: CreateMaterialUsageData): Promise<ApiResponse<MaterialUsage>> {
    return await api.post(this.BASE_URL, data);
  }

  /**
   * 更新材料使用记录
   */
  static async updateMaterialUsage(id: string, data: UpdateMaterialUsageData): Promise<ApiResponse<MaterialUsage>> {
    return await api.put(`${this.BASE_URL}/${id}`, data);
  }

  /**
   * 删除材料使用记录
   */
  static async deleteMaterialUsage(id: string): Promise<ApiResponse> {
    return await api.delete(`${this.BASE_URL}/${id}`);
  }

  /**
   * 获取材料使用统计汇总
   */
  static async getMaterialUsageSummary(params: MaterialUsageQueryParams = {}): Promise<ApiResponse<MaterialUsageSummary>> {
    const queryParams = new URLSearchParams();
    
    if (params.projectId) queryParams.append('projectId', params.projectId);
    if (params.supplierId) queryParams.append('supplierId', params.supplierId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const url = `${this.BASE_URL}/stats/summary?${queryParams.toString()}`;
    return await api.get(url);
  }

  /**
   * 导出材料使用列表Excel
   */
  static async exportExcel(params: MaterialUsageQueryParams = {}): Promise<void> {
    try {
      const response = await api.get(`${this.BASE_URL}/export/excel`, { 
        params,
        responseType: 'blob' 
      });
      // api拦截器已经返回了response.data，所以这里response就是Blob对象
      this.downloadFile(response as unknown as Blob, '材料使用记录列表.xlsx');
    } catch (error) {
      console.error('导出Excel失败:', error);
      throw error;
    }
  }

  /**
   * 导出材料使用列表PDF
   */
  static async exportPDF(params: MaterialUsageQueryParams = {}): Promise<void> {
    try {
      const response = await api.get(`${this.BASE_URL}/export/pdf`, { 
        params,
        responseType: 'blob' 
      });
      // api拦截器已经返回了response.data，所以这里response就是Blob对象
      this.downloadFile(response as unknown as Blob, '材料使用记录列表.pdf');
    } catch (error) {
      console.error('导出PDF失败:', error);
      throw error;
    }
  }

  /**
   * 通用文件下载方法
   */
  private static downloadFile(data: Blob, filename: string) {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * 获取材料名称列表（用于自动完成）
   */
  static async getMaterialNames(): Promise<ApiResponse<string[]>> {
    // 这里可以从后端获取已使用过的材料名称列表
    // 暂时返回一些常用的材料名称
    return Promise.resolve({
      success: true,
      data: [
        '水泥',
        '钢筋',
        '砂子',
        '石子',
        '砖块',
        '木材',
        '钢管',
        '电线',
        '涂料',
        '瓷砖',
        '玻璃',
        '保温材料',
        '防水材料',
        '胶水',
        '螺丝',
        '钉子',
        '管件',
        '阀门',
        '开关',
        '插座'
      ]
    });
  }

  /**
   * 获取常用单位列表
   */
  static async getCommonUnits(): Promise<ApiResponse<string[]>> {
    return Promise.resolve({
      success: true,
      data: [
        '吨',
        '千克',
        '克',
        '立方米',
        '平方米',
        '米',
        '厘米',
        '毫米',
        '个',
        '只',
        '根',
        '张',
        '块',
        '袋',
        '桶',
        '瓶',
        '盒',
        '包',
        '卷',
        '套'
      ]
    });
  }

  /**
   * 单条材料提交（带同步）
   */
  static async submitSingle(data: {
    batchSubmissionId: string;
    materialIndex: number;
    projectId: string;
    usageDate: string;
    material: {
      materialName: string;
      specification?: string;
      quantity: number;
      unit: string;
      unitPrice?: number;
      remark?: string;
    };
    userId: string;
  }): Promise<ApiResponse<{
    usageRecordId: string;
    priceConfigId?: string;
    presetDataId: string;
    operations: any;
  }>> {
    return await api.post(`${this.BASE_URL}/submit-single`, data);
  }
}
